import React from 'react';
import { X, Calendar, Clock, MapPin, Users, Euro, Tag, Globe, User, Send } from 'lucide-react';
import type { Tables } from '../lib/types';

interface EventDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: Tables<'events'> | null;
}

const EventDetailModal: React.FC<EventDetailModalProps> = ({ isOpen, onClose, event }) => {
  if (!isOpen || !event) return null;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return '-';
    return timeStr.substring(0, 5);
  };

  const formatPrice = (price: number | null) => {
    if (price === null || price === undefined) return '-';
    return `${Number(price).toFixed(2)} €`;
  };

  const formatCreatedAt = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'sold-out + lista attesa': return 'Sold-out + Lista Attesa';
      case 'quasi completo': return 'Quasi Completo';
      case 'posti limitati': return 'Posti Limitati';
      default: return 'In Programma';
    }
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'sold-out + lista attesa': return 'bg-red-100 text-red-800';
      case 'quasi completo': return 'bg-orange-100 text-orange-800';
      case 'posti limitati': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-green-100 text-green-800';
    }
  };

  const participantCount = event.participant_count || 0;
  const maxParticipants = event.max_participants || 0;
  const fillPercentage = maxParticipants > 0 ? Math.min((participantCount / maxParticipants) * 100, 100) : 0;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative">
          {(event as any).image_url ? (
            <div className="relative h-48">
              <img
                src={(event as any).image_url}
                alt={event.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors bg-black/30 rounded-full p-1.5 hover:bg-black/50"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 p-6 pr-10">
                <h2 className="text-2xl font-bold text-white">{event.name}</h2>
                <div className="flex items-center gap-3 mt-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusStyle(event.status)}`}>
                    {getStatusLabel(event.status)}
                  </span>
                  {(event as any).matches_sent && (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 flex items-center gap-1">
                      <Send className="h-3 w-3" />
                      Match Inviati
                    </span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-rose-500 to-pink-600 p-6 relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors bg-white/20 rounded-full p-1.5 hover:bg-white/30"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="pr-10">
                <h2 className="text-2xl font-bold text-white">{event.name}</h2>
                <div className="flex items-center gap-3 mt-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusStyle(event.status)}`}>
                    {getStatusLabel(event.status)}
                  </span>
                  {(event as any).matches_sent && (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 flex items-center gap-1">
                      <Send className="h-3 w-3" />
                      Match Inviati
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="bg-rose-100 p-2 rounded-lg">
                <Calendar className="h-5 w-5 text-rose-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Data</p>
                <p className="text-sm font-semibold text-gray-900 capitalize">{formatDate(event.date)}</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Ora</p>
                <p className="text-sm font-semibold text-gray-900">{formatTime(event.time)}</p>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
            <div className="bg-emerald-100 p-2 rounded-lg">
              <MapPin className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">Location</p>
              <p className="text-sm font-semibold text-gray-900">{event.location || '-'}</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
            <div className="bg-sky-100 p-2 rounded-lg">
              <Globe className="h-5 w-5 text-sky-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">Città</p>
              <p className="text-sm font-semibold text-gray-900">{event.city}</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
            <div className="bg-amber-100 p-2 rounded-lg">
              <Tag className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">Fascia d'Età</p>
              <p className="text-sm font-semibold text-gray-900">{event.age_range} anni</p>
            </div>
          </div>

          <div className="p-3 bg-gray-50 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="bg-teal-100 p-2 rounded-lg">
                <Users className="h-5 w-5 text-teal-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 font-medium">Partecipanti</p>
                <p className="text-sm font-semibold text-gray-900">{participantCount} / {maxParticipants}</p>
              </div>
            </div>
            <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  fillPercentage >= 100 ? 'bg-red-500' : fillPercentage >= 75 ? 'bg-orange-500' : 'bg-emerald-500'
                }`}
                style={{ width: `${fillPercentage}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="bg-cyan-100 p-2 rounded-lg">
                <Euro className="h-5 w-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Prezzo Uomini</p>
                <p className="text-sm font-semibold text-gray-900">{formatPrice(event.men_price)}</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="bg-pink-100 p-2 rounded-lg">
                <Euro className="h-5 w-5 text-pink-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Prezzo Donne</p>
                <p className="text-sm font-semibold text-gray-900">{formatPrice(event.women_price)}</p>
              </div>
            </div>
          </div>

          {(event as any).creator?.name && (
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="bg-slate-100 p-2 rounded-lg">
                <User className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Organizzatore</p>
                <p className="text-sm font-semibold text-gray-900">{(event as any).creator.name}</p>
              </div>
            </div>
          )}

          {(event as any).collaborator?.name && (
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="bg-violet-100 p-2 rounded-lg">
                <User className="h-5 w-5 text-violet-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Collaboratore</p>
                <p className="text-sm font-semibold text-gray-900">{(event as any).collaborator.name}</p>
              </div>
            </div>
          )}

          <div className="pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-400">
              Creato il {formatCreatedAt(event.created_at)}
              {event.updated_at && event.updated_at !== event.created_at && (
                <> &middot; Aggiornato il {formatCreatedAt(event.updated_at)}</>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailModal;
